import { Component, EventEmitter, Input, Output } from '@angular/core';

// Company-page step 5 (License Preferences), part 4 of 4 (22b-4d step 6b):
// the "Reports" section of the old data-step="5" pane (company branding +
// header/small-header logo upload), previously company-page.html:446-607.
//
// Business logic (uploadCompanyLogo) stays on the root legacy controller
// unchanged - this component only renders bound state (preferences by
// reference) and forwards click/change actions via @Output emitters, same
// pattern as company-page-step-license-scoring/-assessments/-jobboards.
//
// The legacy `fileModel` directive's image-dimension validation (header logo
// 1035-1045 x 335-345, small header logo 155-165 x 45-55) is ported inline
// here instead of forwarding raw file-input events to the root controller,
// since the validation is pure presentation logic scoped to this component's
// own preview state - only a validated File (or undefined, on a rejected
// file) is ever emitted to the root, matching the old directive's
// `modelSetter` behavior exactly. The old template's `image="headerLogoRef"`/
// `smallHeaderLogoRef` binding relied on a nonexistent `image` directive
// (grepped - no such directive is registered anywhere in the app) and
// `headerLogoRef`/`smallHeaderLogoRef` are never assigned in the legacy
// controller either, so that preview path was always dead; this port keeps
// the same effectively-dead-preview behavior (saved S3 logo or placeholder
// only) rather than inventing a new live-preview feature.
@Component({
  selector: 'app-company-page-step-license',
  templateUrl: './company-page-step-license.component.html'
})
export class CompanyPageStepLicenseComponent {

  @Input() typeOfCompany: string;
  @Input() isUserSuperUserOr4dot5Admin: boolean;
  @Input() preferences: any = {};
  @Input() savedAssessmentWorkFlow: any = {};
  @Input() headerLogo: any;
  @Input() smallHeaderLogo: any;
  @Input() uploadCompanyHeaderLogoInProgress: boolean;
  @Input() uploadCompanySmallHeaderLogoInProgress: boolean;

  @Output() enableBrandingChangeCallback = new EventEmitter<void>();
  @Output() enableSmallLogoChangeCallback = new EventEmitter<void>();
  @Output() headerLogoFileChangeCallback = new EventEmitter<File | undefined>();
  @Output() smallHeaderLogoFileChangeCallback = new EventEmitter<File | undefined>();
  @Output() uploadLogoCallback = new EventEmitter<boolean>();

  headerLogoPreviewError: string;
  smallHeaderLogoPreviewError: string;

  onEnableBrandingChange() {
    this.enableBrandingChangeCallback.emit();
  }

  onEnableSmallLogo() {
    this.enableSmallLogoChangeCallback.emit();
  }

  uploadCompanyLogo(isSmallHeaderLogo: boolean) {
    this.uploadLogoCallback.emit(isSmallHeaderLogo);
  }

  onHeaderLogoFileSelected(event: Event) {
    this.handleFileSelected(event, true);
  }

  onSmallHeaderLogoFileSelected(event: Event) {
    this.handleFileSelected(event, false);
  }

  private handleFileSelected(event: Event, isHeaderLogo: boolean) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) {
      return;
    }

    const imageSizeText = isHeaderLogo ? '(1035-1045) x (335-345)' : '(155-165) x (45-55)';

    if (!(/^image\/(png|jpe?g|gif)$/).test(file.type)) {
      this.setPreviewError(isHeaderLogo, `Unsupported format <br>${file.name}`);
      this.emitFileChange(isHeaderLogo, undefined);
      input.value = '';
      return;
    }

    const img = new Image();
    const objectUrl = window.URL.createObjectURL(file);
    img.addEventListener('load', () => window.URL.revokeObjectURL(objectUrl));
    img.src = objectUrl;

    setTimeout(() => {
      const validSize = isHeaderLogo
        ? (img.width >= 1035 && img.width <= 1045 && img.height >= 335 && img.height <= 345)
        : (img.width >= 155 && img.width <= 165 && img.height >= 45 && img.height <= 55);

      if (validSize) {
        this.setPreviewError(isHeaderLogo, '');
        this.emitFileChange(isHeaderLogo, file);
      } else {
        this.setPreviewError(isHeaderLogo, `Selected image size: ${img.width}x${img.height}. Accepted size: ${imageSizeText}.`);
        this.emitFileChange(isHeaderLogo, undefined);
      }
      input.value = '';
    }, 300);
  }

  private setPreviewError(isHeaderLogo: boolean, message: string) {
    if (isHeaderLogo) {
      this.headerLogoPreviewError = message;
    } else {
      this.smallHeaderLogoPreviewError = message;
    }
  }

  private emitFileChange(isHeaderLogo: boolean, file: File | undefined) {
    if (isHeaderLogo) {
      this.headerLogoFileChangeCallback.emit(file);
    } else {
      this.smallHeaderLogoFileChangeCallback.emit(file);
    }
  }
}
